import { DiscriminantMap } from 'views/order-feed/features/my-supply-plan/types/google-tag-manager/shared.type';

interface BaseOrderFeedEvent extends SupplyPlanEvent {
  event: 'order_feed';
  orderEvent: OrderEvent;
  orderType?: OrderType;
  supplyPlanType: SupplyPlanType;
}

/**
 * ldValue1, newValue1 are previous and new total order quantity
 */
interface UpdateOrderEvent extends BaseOrderFeedEvent {
  orderEvent: Extract<OrderEvent, 'update'>;
  orderNumber: string;
  oldValue1: string;
  newValue1: string;
}

//#region Button click events within Order Feed
/**
 * When the edit button is clicked to begin editing an order. Not to be confused with Update Order
 */
interface EditOrderEvent extends BaseOrderFeedEvent {
  orderEvent: Extract<OrderEvent, 'edit'>;
  orderNumber: string;
}

interface RemoveOrderLineEvent extends BaseOrderFeedEvent {
  orderEvent: Extract<OrderEvent, 'remove_order_line'>;
}

interface AddOrderLineEvent extends BaseOrderFeedEvent {
  orderEvent: Extract<OrderEvent, 'add_order_line'>;
}

interface AdjustSettingsEvent extends BaseOrderFeedEvent {
  orderEvent: Extract<OrderEvent, 'adjust_settings'>;
}

interface RepeatOrderEvent extends BaseOrderFeedEvent {
  orderEvent: Extract<OrderEvent, 'repeat'>;
  orderNumber: string;
}

interface SkipOrderEvent extends BaseOrderFeedEvent {
  orderEvent: Extract<OrderEvent, 'skip'>;
}

interface UnskipOrderEvent extends BaseOrderFeedEvent {
  orderEvent: Extract<OrderEvent, 'unskip'>;
}

interface EditOrderDetailsEvent extends BaseOrderFeedEvent {
  orderEvent: Extract<OrderEvent, 'edit_order_details'>;
}

/**
 * Forecast, open slot, order
 */
interface DropdownEvent extends BaseOrderFeedEvent {
  orderEvent: Extract<OrderEvent, 'dropdown'>;
  orderType: OrderType;
  orderNumber?: string; // if order is clicked
}

//#endregion Button click events within Order Feed

interface DeleteOrderEvent extends BaseOrderFeedEvent {
  orderEvent: Extract<OrderEvent, 'delete'>;
  orderNumber: string;
}

interface CreateOrderEvent extends BaseOrderFeedEvent {
  orderEvent: Extract<OrderEvent, 'create'>;
  orderNumber?: string; // After order is created, we have an order number
}

interface ViewOrderEvent extends BaseOrderFeedEvent {
  orderEvent: Extract<OrderEvent, 'view'>;
  orderNumber: string;
}

interface OverrideForecastEvent extends BaseOrderFeedEvent {
  orderEvent: Extract<OrderEvent, 'override_forecast'>;
  oldValue1: string;
  newValue1: string;
  dailyConsumptionCalculationMethod: string; // ConsumptionAdjustmentMethod
  consumptionRateAdjustmentDays: string; // consumptionRateAdjustmentDays
}

export interface ViewSiteEvent extends SupplyPlanEvent {
  event: 'supplyPlanViewSite';
  alert: string; // comma separated string of alerts belonging to a site.
  supplyPlanType: SupplyPlanType;
  siteStatus: 'active' | 'inactive';
}

export type OrderFeedEvent =
  | UpdateOrderEvent
  | DeleteOrderEvent
  | CreateOrderEvent
  | RepeatOrderEvent
  | EditOrderEvent
  | ViewOrderEvent
  | AddOrderLineEvent
  | RemoveOrderLineEvent
  | AdjustSettingsEvent
  | EditOrderDetailsEvent
  | DropdownEvent
  | SkipOrderEvent
  | UnskipOrderEvent
  | OverrideForecastEvent;

//#region Base Types

export enum OrderType {
  FORECAST = 'forecast',
  SKIPPED = 'skipped',
  CONFIRMED = 'confirmed',
  OPEN = 'open',
}

export enum SupplyPlanType {
  OFD2 = 'OFD2',
  OFD1 = 'OFD1',
  MANUAL = 'manual',
}

export interface SupplyPlanEvent {
  companyId: string; // bluviu company id
  siteId: string; // bluviu site id
  siteErpId: string; // site id from external
  companyErpId: string; // company ID from external
  pageUrl: string;
}

export type OrderEvent =
  | 'view'
  | 'edit'
  | 'delete'
  | 'repeat'
  | 'update'
  | 'create'
  | 'dropdown'
  | 'add_order_line'
  | 'remove_order_line'
  | 'edit_order_details'
  | 'adjust_settings'
  | 'override_forecast'
  | 'skip'
  | 'unskip';

//#endregion Base Types

export type OrderFeedEventMap = DiscriminantMap<OrderFeedEvent, 'orderEvent'>;

// 🔧 Utility to strip shared fields, as these are handled by the createInputSettingsEvent function
export type PartialOrderEventFields<T> = Omit<T, keyof SupplyPlanEvent | 'event' | 'orderEvent'>;
